import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Volume2, Copy, MapPin, Bell, Download, Trash2, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { speak } from "@/lib/audio";
import { setOrbTheme, setOrbState } from "@/lib/orb";
import { ORB_THEMES } from "@/lib/orbThemes";
import { AuroraOrb } from "@/components/AuroraOrb";
import { enablePush, shareLocation, useInstallPrompt } from "@/lib/pwa";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const VOICES = ["italiana", "coral", "nova", "shimmer", "sage", "onyx", "echo", "ash"];

const Row = ({ title, desc, children }) => (
  <div className="flex items-center justify-between gap-4 py-3.5 border-b border-cyan-500/10 last:border-0">
    <div><p className="text-sm text-white font-medium">{title}</p>{desc && <p className="text-xs text-slate-500 mt-0.5">{desc}</p>}</div>
    {children}
  </div>
);

const installHint = (standalone, isIOS) => {
  if (standalone) return "App già installata";
  return isIOS ? "Safari → Condividi → Aggiungi a schermata Home" : "Aggiungi Aurora ON alla schermata Home";
};

export const SettingsView = ({ user, setUser, logout }) => {
  const s = user.settings || {};
  const [ref, setRef] = useState(null);
  const [profile, setProfile] = useState({ name: user.name, phone: user.phone || "" });
  const [preview, setPreview] = useState(null);
  const { can, install, isIOS, standalone } = useInstallPrompt();
  const [itAvail, setItAvail] = useState(true);
  useEffect(() => { api.get("/referral").then((r) => setRef(r.data)); api.get("/tts/voices").then((r) => setItAvail(r.data.italiana_available)); }, []);

  const save = async (patch) => {
    try { const { data } = await api.patch("/me/settings", patch); setUser(data); } catch (e) { toast.error(errMsg(e)); }
  };
  const saveProfile = async () => {
    try { const { data } = await api.patch("/me/profile", profile); setUser(data); toast.success("Profilo aggiornato"); } catch (e) { toast.error(errMsg(e)); }
  };
  const tryVoice = async (v) => {
    setPreview(v);
    try { await speak(`Ciao ${user.name.split(" ")[0]}, sono Aurora con la voce ${v}.`, v); } catch (_) { toast.error("Riproduzione non riuscita"); }
    setPreview(null);
  };
  const inviteLink = ref ? `${window.location.origin}/registrati?ref=${ref.invite_code}` : "";
  const del = async () => {
    try { await api.delete("/me"); toast.success("Account eliminato"); logout(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-6 space-y-5" data-testid="settings-view">
      <div><p className="hud-tag mb-2">Configurazione</p><h2 className="font-heading text-3xl sm:text-4xl font-bold text-white">Impostazioni</h2></div>

      <div className="hud-card p-5">
        <h3 className="font-heading text-xl text-white mb-1">Volto della sfera</h3>
        <p className="text-sm text-slate-400 mb-4">Scegli i colori della Sfera Aurora.</p>
        <div className="flex flex-col sm:flex-row gap-5 items-center">
          <AuroraOrb size={170} testId="settings-orb-preview" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 flex-1 w-full">
            {Object.entries(ORB_THEMES).map(([id, th]) => {
              const sel = (s.orb_theme || "aurora") === id;
              return (
                <button key={id} onClick={() => { setOrbTheme(id); setOrbState("idle"); save({ orb_theme: id }); }} data-testid={`orb-theme-${id}`}
                  className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors ${sel ? "border-amber-300/70 bg-amber-400/10" : "border-cyan-500/20 hover:border-cyan-400/50"}`}>
                  <span className="flex -space-x-1.5">{th.swatch.map((c) => <span key={c} className="w-5 h-5 rounded-full border border-black/40" style={{ background: c, boxShadow: `0 0 10px ${c}` }} />)}</span>
                  <span className="text-sm text-white">{th.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="hud-card p-5">
        <h3 className="font-heading text-xl text-white mb-3">Voce di Aurora</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
          {VOICES.map((v) => (
            <div key={v} className={`rounded-xl border p-2.5 text-center cursor-pointer transition-colors ${s.tts_voice === v ? "border-amber-300/70 bg-amber-400/10" : "border-cyan-500/20 hover:border-cyan-400/50"}`}
              onClick={() => save({ tts_voice: v })} data-testid={`voice-option-${v}`}>
              <p className="text-sm capitalize text-white">{v === "italiana" ? "Italiana" : v}{v === "italiana" && <span className="block text-[10px] text-emerald-300">{itAvail ? "madrelingua" : "in attivazione"}</span>}</p>
              <button onClick={(e) => { e.stopPropagation(); tryVoice(v); }} className="mt-1.5 text-cyan-300 hover:text-amber-300" data-testid={`voice-preview-${v}`}>
                {preview === v ? <Loader2 size={14} className="animate-spin mx-auto" /> : <Volume2 size={14} className="mx-auto" />}
              </button>
            </div>
          ))}
        </div>
        <div className="mt-3">
          <Row title="Benvenuto vocale" desc="Aurora ti saluta a voce all'apertura dell'app"><Switch checked={s.welcome_voice !== false} onCheckedChange={(v) => save({ welcome_voice: v })} data-testid="switch-welcome-voice" /></Row>
          <Row title="Invio vocale rapido" desc="La dettatura invia subito il messaggio"><Switch checked={!!s.quick_voice_send} onCheckedChange={(v) => save({ quick_voice_send: v })} data-testid="switch-quick-voice" /></Row>
        </div>
      </div>

      <div className="hud-card p-5">
        <h3 className="font-heading text-xl text-white mb-1">Privacy · Autodistruzione</h3>
        <p className="text-sm text-slate-400 mb-3">Le conversazioni inattive vengono cancellate automaticamente e per sempre.</p>
        <div className="flex flex-wrap gap-2" data-testid="auto-delete-options">
          {[[0, "Mai"], [1, "Dopo 24 ore"], [7, "Dopo 7 giorni"], [30, "Dopo 30 giorni"]].map(([d, l]) => (
            <button key={d} onClick={() => save({ auto_delete_days: d })} data-testid={`auto-delete-${d}`}
              className={`rounded-full px-4 py-2 text-xs border transition-colors ${(s.auto_delete_days || 0) === d ? "border-emerald-400/70 bg-emerald-500/10 text-emerald-200" : "border-cyan-500/25 text-slate-300 hover:border-cyan-400/50"}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="hud-card p-5">
        <h3 className="font-heading text-xl text-white mb-3">Profilo</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="profile-name-input" className="bg-[#050b18]/80 border border-cyan-500/25 rounded-xl px-4 py-2.5 text-sm outline-none" />
          <input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="Telefono" data-testid="profile-phone-input" className="bg-[#050b18]/80 border border-cyan-500/25 rounded-xl px-4 py-2.5 text-sm outline-none" />
        </div>
        <p className="text-xs text-slate-500 mt-2">Email: {user.email}</p>
        <button onClick={saveProfile} data-testid="profile-save-btn" className="btn-aurora mt-3 rounded-xl px-5 py-2 text-xs uppercase">Salva</button>
      </div>

      <div className="hud-card p-5">
        <h3 className="font-heading text-xl text-white mb-1">Invita amici</h3>
        <p className="text-sm text-slate-400 mb-3">Condividi il tuo codice invito. Amici invitati: <span className="text-amber-300" data-testid="referral-count">{ref?.invited?.length ?? 0}</span></p>
        <div className="flex flex-wrap gap-2 items-center">
          <span className="font-mono text-lg tracking-widest text-aurora px-4 py-2 rounded-xl border border-cyan-500/25 bg-[#050b18]" data-testid="referral-code">{ref?.invite_code}</span>
          <button onClick={() => { navigator.clipboard.writeText(inviteLink); toast.success("Link invito copiato"); }} data-testid="referral-copy-btn" className="btn-ghost-hud rounded-full px-4 py-2 text-xs flex items-center gap-2"><Copy size={14} /> Copia link</button>
        </div>
      </div>

      <div className="hud-card p-5">
        <h3 className="font-heading text-xl text-white mb-2">Permessi & App</h3>
        <Row title="Posizione" desc="Salva la tua ultima posizione (con permesso)">
          <button onClick={() => shareLocation().then(() => toast.success("Posizione aggiornata")).catch((e) => toast.error(e.message))} data-testid="btn-share-location" className="btn-ghost-hud rounded-full px-4 py-2 text-xs flex items-center gap-2"><MapPin size={14} /> Condividi</button>
        </Row>
        <Row title="Notifiche push" desc="Ricevi messaggi e avvisi dal team Aurora">
          <button onClick={() => enablePush().then(() => toast.success("Notifiche attivate")).catch((e) => toast.error(e.message))} data-testid="btn-enable-push" className="btn-ghost-hud rounded-full px-4 py-2 text-xs flex items-center gap-2"><Bell size={14} /> Attiva</button>
        </Row>
        <Row title="Installa l'app" desc={installHint(standalone, isIOS)}>
          {can && <button onClick={install} data-testid="btn-install-settings" className="btn-ghost-hud rounded-full px-4 py-2 text-xs flex items-center gap-2"><Download size={14} /> Installa</button>}
        </Row>
      </div>

      {user.role !== "superadmin" && (
        <div className="hud-card p-5 border-rose-500/30">
          <h3 className="font-heading text-xl text-white mb-1">Elimina account</h3>
          <p className="text-sm text-slate-400 mb-3">Cancella definitivamente account, conversazioni, immagini, documenti e messaggi.</p>
          <AlertDialog>
            <AlertDialogTrigger asChild><button data-testid="btn-delete-account" className="rounded-full px-4 py-2 text-xs border border-rose-500/50 text-rose-300 hover:bg-rose-500/10 flex items-center gap-2"><Trash2 size={14} /> Elimina il mio account</button></AlertDialogTrigger>
            <AlertDialogContent className="bg-[#0a1224] border-rose-500/30">
              <AlertDialogHeader><AlertDialogTitle>Confermi l'eliminazione?</AlertDialogTitle><AlertDialogDescription>Questa azione è irreversibile.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel data-testid="delete-cancel-btn">Annulla</AlertDialogCancel><AlertDialogAction onClick={del} className="bg-rose-600 hover:bg-rose-500" data-testid="delete-confirm-btn">Elimina</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </div>
  );
};
